import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../context/CompanyContext';
import { Upload, FileSpreadsheet, Check, AlertCircle, ArrowRight, X, RefreshCw, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UPLOAD_TYPES = [
  { value: 'timecards', label: 'Timecards', description: 'Employee time entries' },
  { value: 'expenses', label: 'Expenses', description: 'Expense records' },
  { value: 'customers', label: 'Customers', description: 'Customer data' },
  { value: 'vendors', label: 'Vendors', description: 'Vendor / supplier list (also AgData Blue Skies vendor files)' },
  { value: 'employees', label: 'Employees', description: 'Employee records' },
  { value: 'invoices', label: 'Invoices', description: 'Invoice data' },
  { value: 'journal', label: 'Journal Entries', description: 'General ledger entries' },
];

const VIEW_PATH = { vendors: ['/view/vendors', 'View vendors'], customers: ['/view/customers', 'View customers'], employees: ['/view/employees', 'View employees'] };

const BulkUpload = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { companyId, selectedCompany } = useCompany();
  const [step, setStep] = useState(1);
  const [uploadType, setUploadType] = useState('');
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [columnMappings, setColumnMappings] = useState({});
  const [processResult, setProcessResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileSelect = useCallback((e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      const ext = droppedFile.name.split('.').pop()?.toLowerCase();
      if (['csv', 'xlsx', 'xls', 'dbf'].includes(ext)) {
        setFile(droppedFile);
        setError(null);
      } else {
        setError('Please upload a CSV, Excel (.xlsx or .xls) or dBase (.dbf) file');
      }
    }
  }, []);

  const handleUpload = async () => {
    if (!file || !uploadType) return;

    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_type', uploadType);
      formData.append('company_id', companyId || 'default');

      const response = await fetch(`${API_URL}/api/upload/preview`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Upload failed');
      }

      const data = await response.json();
      setPreview(data);

      // Initialize mappings from AI suggestions
      const initialMappings = {};
      data.ai_mappings.forEach((mapping) => {
        if (mapping.target_field !== 'skip') {
          initialMappings[mapping.source_column] = mapping.target_field;
        }
      });
      setColumnMappings(initialMappings);

      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleProcess = async () => {
    if (!preview) return;

    setIsUploading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/upload/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          upload_id: preview.upload_id,
          column_mappings: columnMappings,
        }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Processing failed');
      }

      const data = await response.json();
      setProcessResult(data);
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleMappingChange = (sourceColumn, targetField) => {
    setColumnMappings((prev) => ({
      ...prev,
      [sourceColumn]: targetField,
    }));
  };

  const resetUpload = () => {
    setStep(1);
    setFile(null);
    setPreview(null);
    setColumnMappings({});
    setProcessResult(null);
    setError(null);
  };

  const getTargetFields = () => {
    const fields = {
      timecards: ['employee_id', 'employee_name', 'date', 'hours_regular', 'hours_overtime', 'cost_center', 'enterprise', 'notes'],
      expenses: ['description', 'amount', 'category', 'vendor', 'expense_date', 'payment_method', 'reference_number', 'notes'],
      customers: ['name', 'company_name', 'email', 'phone', 'address', 'city', 'state', 'zip_code', 'country', 'notes'],
      vendors: ['vendor_no', 'name', 'dba_name', 'contact', 'email', 'phone', 'fax', 'address', 'address2', 'city_state', 'zip', 'tax_id', 'default_gl_account', 'notes'],
      employees: ['first_name', 'last_name', 'email', 'phone', 'department', 'position', 'hire_date', 'pay_rate', 'pay_type'],
      invoices: ['customer_id', 'customer_name', 'invoice_date', 'due_date', 'description', 'quantity', 'unit_price', 'amount', 'notes'],
      journal: ['date', 'description', 'account_number', 'account_name', 'debit', 'credit', 'reference'],
    };
    return ['skip', ...(fields[uploadType] || [])];
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 0.9) return <Badge className="bg-green-100 text-green-800">High match</Badge>;
    if (confidence >= 0.7) return <Badge className="bg-yellow-100 text-yellow-800">Good match</Badge>;
    if (confidence >= 0.5) return <Badge className="bg-orange-100 text-orange-800">Possible match</Badge>;
    return <Badge className="bg-gray-100 text-gray-600">No match</Badge>;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="bulk-upload-modal">
        <CardHeader className="border-b flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-sky-600" />
              Bulk Upload with AI Mapping
            </CardTitle>
            <CardDescription>
              Step {step} of 3: {step === 1 ? 'Select file' : step === 2 ? 'Review mappings' : 'Complete'}
            </CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-upload-modal">
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>

        <CardContent className="flex-1 overflow-auto p-6">
          {/* Progress */}
          <div className="mb-6">
            <Progress value={step * 33.33} className="h-2" />
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span className={step >= 1 ? 'text-sky-600 font-medium' : ''}>Upload</span>
              <span className={step >= 2 ? 'text-sky-600 font-medium' : ''}>Map Columns</span>
              <span className={step >= 3 ? 'text-sky-600 font-medium' : ''}>Complete</span>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          )}

          {/* Step 1: File Selection */}
          {step === 1 && (
            <div className="space-y-6">
              <p className="text-sm rounded-md border border-sky-200 bg-sky-50 text-sky-900 px-3 py-2" data-testid="import-target-company">
                Records will be imported into <b>{selectedCompany?.name || 'the default company'}</b>. To import into another company, close this window and choose it in the company list at the top of the page.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What type of data are you uploading?
                </label>
                <Select value={uploadType} onValueChange={setUploadType}>
                  <SelectTrigger className="w-full" data-testid="upload-type-select">
                    <SelectValue placeholder="Select data type" />
                  </SelectTrigger>
                  <SelectContent>
                    {UPLOAD_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex flex-col">
                          <span>{type.label}</span>
                          <span className="text-xs text-gray-500">{type.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  file ? 'border-sky-400 bg-sky-50' : 'border-gray-300 hover:border-sky-400'
                }`}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                data-testid="file-dropzone"
              >
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileSpreadsheet className="w-10 h-10 text-sky-600" />
                    <div className="text-left">
                      <p className="font-medium text-gray-900">{file.name}</p>
                      <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setFile(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">Drag and drop your file here, or click to browse</p>
                    <p className="text-sm text-gray-500 mb-4">Supports CSV, Excel (.xlsx, .xls) and dBase (.dbf)</p>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls,.dbf"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="file-upload"
                    />
                    <Button asChild variant="outline">
                      <label htmlFor="file-upload" className="cursor-pointer" data-testid="file-select-btn">
                        Select File
                      </label>
                    </Button>
                  </>
                )}
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleUpload}
                  disabled={!file || !uploadType || isUploading}
                  className="bg-sky-600 hover:bg-sky-700"
                  data-testid="upload-btn"
                >
                  {isUploading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      Upload & Analyze
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Column Mapping */}
          {step === 2 && preview && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">{preview.file_name}</h3>
                  <p className="text-sm text-gray-500">{preview.total_rows} rows detected</p>
                </div>
                <Badge variant="outline" className="text-sky-600 border-sky-600">
                  AI-Mapped
                </Badge>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b">
                  <h4 className="font-medium text-sm text-gray-700">Column Mappings</h4>
                  <p className="text-xs text-gray-500">AI has suggested mappings. Review and adjust as needed.</p>
                </div>
                <div className="divide-y">
                  {preview.columns.map((col, idx) => {
                    const aiMapping = preview.ai_mappings.find((m) => m.source_column === col);
                    return (
                      <div key={col} className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{col}</p>
                          <p className="text-xs text-gray-500 truncate">
                            Sample: {preview.sample_data[0]?.[col] || '-'}
                          </p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="w-48 flex-shrink-0">
                          <Select
                            value={columnMappings[col] || 'skip'}
                            onValueChange={(value) => handleMappingChange(col, value)}
                          >
                            <SelectTrigger className="w-full text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {getTargetFields().map((field) => (
                                <SelectItem key={field} value={field}>
                                  {field === 'skip' ? '— Skip this column —' : field.replace(/_/g, ' ')}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-24 flex-shrink-0">
                          {aiMapping && getConfidenceBadge(aiMapping.confidence)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Preview Table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b">
                  <h4 className="font-medium text-sm text-gray-700">Data Preview</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        {preview.columns.map((col) => (
                          <th key={col} className="px-4 py-2 text-left font-medium text-gray-600 whitespace-nowrap">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {preview.sample_data.slice(0, 5).map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          {preview.columns.map((col) => (
                            <td key={col} className="px-4 py-2 text-gray-700 whitespace-nowrap">
                              {row[col] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {isUploading && (
                <div className="rounded-lg border border-sky-200 bg-sky-50 p-3" data-testid="import-progress" role="status">
                  <p className="text-sm font-medium text-sky-900 mb-2">Importing {preview.total_rows} records… please keep this window open.</p>
                  <div className="h-2 w-full overflow-hidden rounded bg-sky-100"><div className="h-full w-1/3 animate-pulse rounded bg-sky-600" /></div>
                </div>
              )}

              <div className="flex justify-between">
                <Button variant="outline" onClick={resetUpload} disabled={isUploading}>
                  Start Over
                </Button>
                <Button
                  onClick={handleProcess}
                  disabled={isUploading}
                  className="bg-sky-600 hover:bg-sky-700"
                  data-testid="process-btn"
                >
                  {isUploading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Process {preview.total_rows} Records
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Complete */}
          {step === 3 && processResult && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Import finished</h3>
              <p className="text-gray-600 mb-6">{processResult.message}</p>

              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-8">
                <div className="bg-green-50 rounded-lg p-4" data-testid="result-created">
                  <p className="text-2xl font-bold text-green-600">{processResult.created}</p>
                  <p className="text-sm text-green-700">Imported</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4" data-testid="result-skipped">
                  <p className="text-2xl font-bold text-amber-600">{processResult.skipped || 0}</p>
                  <p className="text-sm text-amber-700">Skipped (already there)</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4" data-testid="result-errors">
                  <p className="text-2xl font-bold text-red-600">{processResult.errors}</p>
                  <p className="text-sm text-red-700">Problems</p>
                </div>
              </div>

              {processResult.skipped > 0 && processResult.skipped_details?.length > 0 && (
                <div className="text-left bg-amber-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-amber-800 mb-2">Skipped, not imported again:</h4>
                  <ul className="text-sm text-amber-800 space-y-1">
                    {processResult.skipped_details.map((d, idx) => (
                      <li key={idx}>Row {d.row}: {d.error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {processResult.errors > 0 && processResult.error_details?.length > 0 && (
                <div className="text-left bg-red-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-red-800 mb-2">These rows were not imported:</h4>
                  <ul className="text-sm text-red-700 space-y-1">
                    {processResult.error_details.map((err, idx) => (
                      <li key={idx}>Row {err.row}: {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={resetUpload}>
                  Upload Another File
                </Button>
                {processResult.created > 0 && VIEW_PATH[processResult.upload_type] && (
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => { onClose(); navigate(VIEW_PATH[processResult.upload_type][0]); }}
                    data-testid="view-imported-btn"
                  >
                    {VIEW_PATH[processResult.upload_type][1]}
                  </Button>
                )}
                <Button onClick={onClose} className="bg-sky-600 hover:bg-sky-700">
                  Done
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BulkUpload;
